import React, { useState, useEffect, useRef, useCallback } from 'react';
import { customerAPI, paymentAPI } from '../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { Search, IndianRupee, CheckCircle, User, Wifi, Tv, Printer } from 'lucide-react';

const fmt = n => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));

const PAYMENT_FOR_OPTIONS = [
  { value: 'broadband_renewal', label: 'Broadband Renewal' },
  { value: 'catv_renewal', label: 'CATV Renewal' },
  { value: 'installation', label: 'Installation Charge' },
  { value: 'security_deposit', label: 'Security Deposit' },
  { value: 'other', label: 'Other' },
];

export default function CashCollectionPage() {
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [form, setForm] = useState({
    paymentFor: 'broadband_renewal',
    amount: '',
    discount: 0,
    notes: '',
    transactionId: '',
    paymentMethod: 'cash',
  });
  const [submitting, setSubmitting] = useState(false);

  const [todayCollections, setTodayCollections] = useState([]);
  const [todayTotal, setTodayTotal] = useState(0);
  const [loadingToday, setLoadingToday] = useState(true);

  const searchTimer = useRef(null);

  const loadToday = useCallback(async () => {
    setLoadingToday(true);
    try {
      const { data } = await paymentAPI.getMyToday();
      setTodayCollections(data.data);
      setTodayTotal(data.total);
    } catch {
      // silent — not critical
    } finally {
      setLoadingToday(false);
    }
  }, []);

  useEffect(() => { loadToday(); }, [loadToday]);

  // Debounced customer search
  useEffect(() => {
    if (!search.trim() || search.length < 2) { setSearchResults([]); return; }
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await customerAPI.getAll({ search, limit: 8, status: 'active' });
        setSearchResults(data.data || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(searchTimer.current);
  }, [search]);

  const selectCustomer = (c) => {
    setSelectedCustomer(c);
    setSearch('');
    setSearchResults([]);

    // Pre-fill payment type and amount from customer's plan
    let paymentFor = 'broadband_renewal';
    let amount = '';
    if (c.customerType === 'catv') paymentFor = 'catv_renewal';
    if (c.broadband?.plan) amount = String(c.broadband.plan.price || '');
    else if (c.catv?.package) { amount = String(c.catv.package.price || ''); paymentFor = 'catv_renewal'; }

    setForm(f => ({ ...f, paymentFor, amount }));
  };

  const totalAmount = parseFloat(form.amount || 0) - (form.discount || 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCustomer) { toast.error('Select a customer first'); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error('Enter a valid amount'); return; }

    setSubmitting(true);
    try {
      await paymentAPI.create({
        customer: selectedCustomer._id,
        ...form,
        amount: parseFloat(form.amount),
        discount: parseFloat(form.discount) || 0,
      });
      toast.success(`Payment of ₹${fmt(totalAmount)} recorded for ${selectedCustomer.name}`);
      setSelectedCustomer(null);
      setForm({ paymentFor: 'broadband_renewal', amount: '', discount: 0, notes: '', transactionId: '', paymentMethod: 'cash' });
      loadToday();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  const expiryBadge = (date) => {
    if (!date) return null;
    const days = Math.ceil((new Date(date) - new Date()) / 86400000);
    if (days < 0) return <span className="badge badge-danger">Expired {Math.abs(days)}d ago</span>;
    if (days === 0) return <span className="badge badge-danger">Expires Today</span>;
    if (days <= 3) return <span className="badge badge-warning">{days}d left</span>;
    if (days <= 7) return <span className="badge badge-info">{days}d left</span>;
    return <span className="badge badge-success">{days}d left</span>;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700 }}>Cash Collection</h2>
          <p className="text-muted text-small">Record cash payments collected from customers</p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>Today's Total</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--success)' }}>₹{fmt(todayTotal)}</div>
          <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>{todayCollections.length} payments</div>
        </div>
      </div>

      <div className="grid-2">
        {/* Left — Search + Payment Form */}
        <div>
          {/* Customer Search */}
          <div className="card mb-4">
            <div className="card-header"><h3>Find Customer</h3></div>
            <div className="card-body">
              <div style={{ position: 'relative' }}>
                <div className="search-input-wrapper">
                  <Search size={14} className="search-icon" />
                  <input
                    className="search-input"
                    placeholder="Search by name, mobile, or Customer ID..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    autoComplete="off"
                  />
                </div>
                {(searchResults.length > 0 || searching) && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                    background: 'var(--card-bg)', border: '1px solid var(--border)',
                    borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,.15)', marginTop: 4
                  }}>
                    {searching ? (
                      <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)', fontSize: '.85rem' }}>Searching...</div>
                    ) : (
                      searchResults.map(c => (
                        <div key={c._id} onClick={() => selectCustomer(c)}
                          style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border)', transition: 'background .1s' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <div style={{ fontWeight: 600, fontSize: '.9rem' }}>{c.name}</div>
                          <div style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>
                            {c.customerId} · {c.mobile} · <span style={{ textTransform: 'capitalize' }}>{c.customerType}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Selected Customer Info */}
              {selectedCustomer && (
                <div style={{ marginTop: 12, padding: 14, background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <User size={14} color="var(--primary)" />
                        <span style={{ fontWeight: 700 }}>{selectedCustomer.name}</span>
                        <span className="badge badge-info" style={{ fontSize: '.7rem' }}>{selectedCustomer.customerId}</span>
                      </div>
                      <div style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginBottom: 6 }}>{selectedCustomer.mobile} · {selectedCustomer.address?.area}</div>
                      {selectedCustomer.broadband?.plan && (
                        <div style={{ fontSize: '.8rem', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                          <Wifi size={12} color="#2563eb" />
                          <span>{selectedCustomer.broadband.plan.name} — ₹{selectedCustomer.broadband.plan.price}/mo</span>
                          {expiryBadge(selectedCustomer.broadband.expiryDate)}
                        </div>
                      )}
                      {selectedCustomer.catv?.package && (
                        <div style={{ fontSize: '.8rem', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                          <Tv size={12} color="#7c3aed" />
                          <span>{selectedCustomer.catv.package.name} — ₹{selectedCustomer.catv.package.price}/mo</span>
                          {expiryBadge(selectedCustomer.catv.expiryDate)}
                        </div>
                      )}
                      {selectedCustomer.outstandingBalance > 0 && (
                        <div style={{ fontSize: '.8rem', color: 'var(--danger)', fontWeight: 600, marginTop: 4 }}>
                          Outstanding: ₹{fmt(selectedCustomer.outstandingBalance)}
                        </div>
                      )}
                    </div>
                    <button onClick={() => setSelectedCustomer(null)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 18, lineHeight: 1 }}>×</button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Payment Form */}
          {selectedCustomer && (
            <div className="card">
              <div className="card-header"><h3>Record Payment</h3></div>
              <div className="card-body">
                <form onSubmit={handleSubmit}>
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">Payment For <span className="required">*</span></label>
                      <select className="form-control" value={form.paymentFor}
                        onChange={e => setForm(f => ({ ...f, paymentFor: e.target.value }))}>
                        {PAYMENT_FOR_OPTIONS.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Payment Method</label>
                      <select className="form-control" value={form.paymentMethod}
                        onChange={e => setForm(f => ({ ...f, paymentMethod: e.target.value }))}>
                        <option value="cash">Cash</option>
                        <option value="upi">UPI</option>
                        <option value="cheque">Cheque</option>
                        <option value="bank_transfer">Bank Transfer</option>
                        <option value="online">Online</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">Amount (₹) <span className="required">*</span></label>
                      <input type="number" className="form-control" min="1" step="0.01"
                        value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                        placeholder="0" required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Discount (₹)</label>
                      <input type="number" className="form-control" min="0" step="0.01"
                        value={form.discount} onChange={e => setForm(f => ({ ...f, discount: parseFloat(e.target.value) || 0 }))}
                        placeholder="0" />
                    </div>
                  </div>

                  {form.paymentMethod !== 'cash' && (
                    <div className="form-group">
                      <label className="form-label">Transaction / Cheque No.</label>
                      <input className="form-control font-mono" value={form.transactionId}
                        onChange={e => setForm(f => ({ ...f, transactionId: e.target.value }))}
                        placeholder="UPI Txn ID / Cheque No." />
                    </div>
                  )}

                  <div className="form-group">
                    <label className="form-label">Notes</label>
                    <input className="form-control" value={form.notes}
                      onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                      placeholder="Optional note..." />
                  </div>

                  {/* Amount summary */}
                  {form.amount > 0 && (
                    <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '12px 14px', marginBottom: 16, fontSize: '.85rem' }}>
                      {form.discount > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, color: 'var(--danger)' }}>
                          <span>Discount</span><span>-₹{fmt(form.discount)}</span>
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '1rem', borderTop: form.discount > 0 ? '1px solid var(--border)' : 'none', paddingTop: form.discount > 0 ? 8 : 0, marginTop: form.discount > 0 ? 4 : 0 }}>
                        <span>Total</span><span style={{ color: 'var(--success)' }}>₹{fmt(totalAmount)}</span>
                      </div>
                    </div>
                  )}

                  <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={submitting}>
                    <CheckCircle size={15} />
                    {submitting ? 'Recording...' : `Record ₹${fmt(totalAmount)} Payment`}
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>

        {/* Right — Today's Collections */}
        <div>
          <div className="card">
            <div className="card-header">
              <h3>Your Collections Today</h3>
              <span style={{ fontSize: '.8rem', color: 'var(--success)', fontWeight: 600 }}>
                ₹{fmt(todayTotal)}
              </span>
            </div>
            <div className="table-container">
              {loadingToday ? (
                <div style={{ textAlign: 'center', padding: 30 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
              ) : todayCollections.length === 0 ? (
                <div className="empty-state" style={{ padding: 40 }}>
                  <IndianRupee size={28} />
                  <p style={{ marginTop: 8, color: 'var(--text-muted)', fontSize: '.85rem' }}>No payments collected today yet.</p>
                </div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Customer</th>
                      <th>For</th>
                      <th>Method</th>
                      <th>Amount</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {todayCollections.map(p => (
                      <tr key={p._id}>
                        <td style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>
                          {format(new Date(p.paymentDate), 'HH:mm')}
                        </td>
                        <td>
                          <div style={{ fontWeight: 500, fontSize: '.85rem' }}>{p.customer?.name}</div>
                          <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>{p.customer?.customerId}</div>
                        </td>
                        <td style={{ fontSize: '.78rem', textTransform: 'capitalize' }}>
                          {p.paymentFor?.replace(/_/g, ' ')}
                        </td>
                        <td>
                          <span className={`badge badge-${p.paymentMethod === 'cash' ? 'success' : 'info'}`} style={{ textTransform: 'capitalize', fontSize: '.7rem' }}>
                            {p.paymentMethod}
                          </span>
                        </td>
                        <td style={{ fontWeight: 700, color: 'var(--success)', fontSize: '.85rem' }}>
                          ₹{fmt(p.totalAmount)}
                        </td>
                        <td>
                          <a href={`/api/v1/payments/${p._id}/receipt`} target="_blank" rel="noreferrer"
                            className="btn btn-secondary btn-sm" title="Print receipt">
                            <Printer size={11} />
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            {todayCollections.length > 0 && (
              <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', fontSize: '.82rem' }}>
                <span style={{ color: 'var(--text-muted)' }}>{todayCollections.length} payments collected</span>
                <span style={{ fontWeight: 700, color: 'var(--success)' }}>Total: ₹{fmt(todayTotal)}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
